/**
 * Landing v3 — the "Share & Study with your friends" tile's picture (Figma
 * 119:3356, user 2026-09-29: "invent names for the study groups and make the
 * avatar all different and floating behind with a fade out effect"): two
 * study groups as the app draws them — the DS `StudyGroupCard`: the folder in
 * the group's colour, two members' faces on it, its decks inside, its study
 * counts — in front of a crowd of friends whose faces rise slowly behind the
 * groups and fade out as they go.
 *
 * Twelve faces, all different (the landing's CC0 portraits, see
 * landing-leaderboard). Each face's rise is a CSS animation of its own
 * length and phase — transform and opacity only — paused off screen and still
 * under reduced motion. The cards are a picture: out of the tab order, their
 * lift on hover kept.
 */
import { type FolderPreview, StudyGroupCard } from '@flashka/shared/ui';

import { useInView } from 'framer-motion';
import { MoreHorizontalIcon } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useStudyActivityBadgeBuilder } from '@/components/utils/study-activity-badges';
import { compactCardClass } from '@/settings/card-size';
import { useDeckColorHex } from '@/utils/deck-color-hex';

import { useFitWidth } from './use-fit-width';

const portrait = (name: string) => `/assets/landing/avatars/${name}.jpg`;
const displayName = (name: string) => name[0].toUpperCase() + name.slice(1);

/** The tray the crowd floats in (Figma 119:3356). */
const TRAY = { w: 448, h: 240 } as const;
/** Two compact study-group tiles, 24px apart (Figma 119:3242 / 119:3309). */
const ROW_W = 180 * 2 + 24;

/**
 * Where each friend floats from (Figma 129:9959 …, px of the tray) and how
 * long one rise takes. The frame's thirteenth face sat wholly behind the first
 * group; the twelve here are the portraits we have.
 */
const FACES = [
  { photo: 'amara', x: 15, y: 27, s: 9.4 },
  { photo: 'jonas', x: -13, y: 163, s: 11.2 },
  { photo: 'chloe', x: 138, y: 219, s: 8.6 },
  { photo: 'mateo', x: 25, y: 220, s: 10.4 },
  { photo: 'sofia', x: 325, y: 194, s: 9.8 },
  { photo: 'tom', x: 384, y: 215, s: 12 },
  { photo: 'imani', x: 206, y: 163, s: 8.8 },
  { photo: 'daniel', x: 132, y: 11, s: 10.8 },
  { photo: 'giulia', x: 263, y: 14, s: 9.2 },
  { photo: 'diego', x: 396, y: 78, s: 11.6 },
  { photo: 'yuna', x: 427, y: 189, s: 10 },
  { photo: 'lucas', x: 427, y: 14, s: 8.2 },
] as const;

/** The two groups: their names invented (i18n) and short — a compact tile's
 *  title has 82px, the ⋯ beside it — their colours two the app offers, their
 *  counts ones that fit the tile's chip row (the frame's 24 · 8 · 33 drops
 *  its third chip on the DS card). */
const GROUPS = [
  {
    key: 'anatomy',
    titleKey: 'landingV3.tiles.friends.groups.anatomy',
    color: 'cyan',
    members: ['giulia', 'lucas'],
    decks: ['🧠', '🦴', '📝'],
    counts: { flashcards: 24, quizzes: 8, oralExams: 6, caseStudies: 0 },
  },
  {
    key: 'pharma',
    titleKey: 'landingV3.tiles.friends.groups.pharma',
    color: 'teal',
    members: ['yuna', 'diego'],
    decks: ['💊', '🧪', '📝'],
    counts: { flashcards: 56, quizzes: 12, oralExams: 9, caseStudies: 0 },
  },
] as const;

export function FriendsGroups() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });
  // The groups' row, scaled down (never up) to a tray narrower than the
  // frame's — a phone's.
  const scale = useFitWidth(ref, ROW_W, 24);
  const colorHex = useDeckColorHex();
  const badgesFor = useStudyActivityBadgeBuilder();
  const groups = useMemo(
    () =>
      GROUPS.map((group) => {
        const hex = colorHex(group.color);
        return {
          ...group,
          hex,
          previews: group.decks.map(
            (emoji, i): FolderPreview =>
              // The last deck is the plain page; the others wear the group's hue.
              i < group.decks.length - 1 ? { emoji, color: hex } : { emoji },
          ),
          members: group.members.map((name) => ({
            name: displayName(name),
            src: portrait(name),
          })),
          badges: badgesFor(group.counts),
        };
      }),
    [colorHex, badgesFor],
  );
  // The `⋯` as a plain glyph: the cards are a picture here, nothing to act on.
  const actionsSlot = (
    <span className="flex size-8 items-center justify-center text-secondary-400">
      <MoreHorizontalIcon className="size-4" />
    </span>
  );

  return (
    <div
      ref={ref}
      aria-hidden
      className="relative flex h-[240px] w-full items-center justify-center overflow-clip rounded-[28px] bg-secondary-100">
      {/* The crowd, behind the groups; it fades out toward the tray's edges. */}
      <div className="lv3-friends-crowd pointer-events-none absolute inset-0">
        {FACES.map((face, i) => (
          <img
            key={face.photo}
            src={portrait(face.photo)}
            alt=""
            width={26}
            height={26}
            loading="lazy"
            decoding="async"
            className="lv3-face-rise absolute h-[26px] w-[26px] rounded-full object-cover"
            style={{
              left: `${(face.x / TRAY.w) * 100}%`,
              top: `${(face.y / TRAY.h) * 100}%`,
              animationDuration: `${face.s}s`,
              animationDelay: `-${((i * 0.37) % 1) * face.s}s`,
              animationPlayState: near ? 'running' : 'paused',
            }}
          />
        ))}
      </div>
      <div
        className="relative flex shrink-0 items-center gap-6"
        style={{ transform: scale < 1 ? `scale(${scale})` : undefined }}>
        {groups.map((group) => (
          <StudyGroupCard
            key={group.key}
            view="grid"
            size="compact"
            className={compactCardClass('compact')}
            title={t(group.titleKey)}
            color={group.hex}
            previews={group.previews}
            members={group.members}
            badges={group.badges}
            actionsSlot={actionsSlot}
            webProps={{ tabIndex: -1 }}
          />
        ))}
      </div>
    </div>
  );
}
